import axios from "axios";
import { traverseComponents } from "./formSchemaUtils";
import { resolveApiBaseUrl } from "./caddieFormioUtils";

const SELECT_TYPES = new Set(["select"]);

function isSelectInput(comp) {
  if (!comp?.input) return false;
  return SELECT_TYPES.has((comp.type || "").toLowerCase());
}

function isInvitationTypesSelect(comp) {
  const label = (comp.label || "").toLowerCase();
  const key = (comp.key || "").toLowerCase();
  if (/invitation\s*type/.test(label)) return true;
  if (/^invitationtypes?$/.test(key) || key === "invitation_types") return true;
  return false;
}

export function formSchemaHasInvitationTypeFormSelects(components) {
  if (!Array.isArray(components)) return false;
  let hasInvitation = false;
  traverseComponents(components, (comp) => {
    if (!isSelectInput(comp)) return;
    if (isInvitationTypesSelect(comp)) hasInvitation = true;
  });
  return hasInvitation;
}

/** Uniforms + Flight side-by-side entitlement block (PERSONAL SETTINGS tab). */
export function formSchemaHasInvitationEntitlementLayout(components) {
  if (!Array.isArray(components)) return false;
  let hasUniforms = false;
  let hasFlight = false;
  traverseComponents(components, (comp) => {
    const key = (comp.key || "").toLowerCase();
    if (key === "uniforms") hasUniforms = true;
    if (key === "flight") hasFlight = true;
  });
  return hasUniforms && hasFlight;
}

const PS_YESNO_KEYS = new Set([
  "uniforms",
  "ticketrequestforgolf",
  "socialevent",
  "radio1",
  "loungepass",
  "gamestimeinsurance",
  "expensescoveredbyigfseeapplicablepolicy",
]);

const PS_STACK_KEYS = new Set([
  "flight",
  "foodbeverage",
  "arrivalsdepartures",
]);

function addCustomClass(comp, hook) {
  const existing = String(comp.customClass || "")
    .split(/\s+/)
    .filter(Boolean);
  if (!existing.includes(hook)) existing.push(hook);
  comp.customClass = existing.join(" ");
}

function componentTreeHasKey(components, wantKey) {
  if (!Array.isArray(components)) return false;
  for (const c of components) {
    if (String(c?.key || "").toLowerCase() === wantKey) return true;
    if (componentTreeHasKey(c?.components, wantKey)) return true;
    if (Array.isArray(c?.columns)) {
      for (const col of c.columns) {
        if (componentTreeHasKey(col?.components, wantKey)) return true;
      }
    }
  }
  return false;
}

function columnsHasKey(comp, wantKey) {
  const cols = Array.isArray(comp?.columns) ? comp.columns : [];
  return cols.some((col) => componentTreeHasKey(col.components, wantKey));
}

/**
 * Stamp render-only CSS hooks for Participant Edit Personal Settings layout.
 * Matches Form Builder: left Yes/No row; right options stacked under label.
 * Does not persist — mutates in-memory schema only.
 *
 * Note: Form.io Columns root element IS `.row` (className = "row formio-component-…").
 * There is no nested `.row` child — CSS must target cols as direct children.
 */
export function stampPersonalSettingsRadioLayoutClasses(components) {
  if (!Array.isArray(components)) return;
  if (!formSchemaHasInvitationEntitlementLayout(components)) return;

  traverseComponents(components, (comp) => {
    const type = (comp.type || "").toLowerCase();

    /* Entitlement Yes/No | Flight block — hook on the columns component itself */
    if (type === "columns" && columnsHasKey(comp, "uniforms") && columnsHasKey(comp, "flight")) {
      addCustomClass(comp, "ps-entitlement-cols");
      return;
    }

    if (type !== "radio") return;
    const key = String(comp.key || "").toLowerCase();
    const label = String(comp.label || "");
    let hook = "";
    if (PS_YESNO_KEYS.has(key)) hook = "ps-yesno-radio";
    else if (PS_STACK_KEYS.has(key) || (key === "radio" && /accommodation/i.test(label))) {
      hook = "ps-stack-radio";
    }
    if (!hook) return;
    addCustomClass(comp, hook);
  });
}

export function isInvitationTypesFormName(formName) {
  return /invitation\s*types?\s*form/i.test(String(formName || ""));
}

function fillSelectOptions(comp, options) {
  if (!options.length) return;
  comp.dataSrc = "values";
  comp.data = { values: options };
  comp.valueProperty = "value";
  comp.template = "<span>{{ item.label }}</span>";
  comp.widget = "choicesjs";
  comp.searchEnabled = true;
  comp.lazyLoad = false;
}

export async function fetchInvitationTypeOptions(apiUrl) {
  const base = resolveApiBaseUrl(apiUrl);
  const res = await axios.get(`${base}/ems/invitation-types?all=true`);
  const list = Array.isArray(res.data) ? res.data : [];
  return list.map((t) => ({
    label: t.name || String(t._id),
    value: String(t._id),
  }));
}

export function injectInvitationTypeFormSelects(components, invitationTypes) {
  if (!Array.isArray(components)) return;

  traverseComponents(components, (comp) => {
    if (!isSelectInput(comp)) return;
    if (isInvitationTypesSelect(comp)) {
      fillSelectOptions(comp, invitationTypes || []);
    }
  });
}

/** Sync inject (no API) — safe to call when options are already loaded. */
export function applyInvitationTypeFormSelects(schema, invitationTypes, formName = "") {
  if (!schema?.components) return schema;

  const isTarget =
    isInvitationTypesFormName(formName) ||
    formSchemaHasInvitationTypeFormSelects(schema.components);

  if (!isTarget || !invitationTypes?.length) return schema;

  const next = JSON.parse(JSON.stringify(schema));
  injectInvitationTypeFormSelects(next.components, invitationTypes);
  return next;
}

/** Label format: "Function (Code)", omitting missing parts. */
export function formatAccreditationCategoryLabel(category) {
  if (!category) return 'Unnamed';
  if (category.function) return category.function;
  if (category.category_name) return category.category_name;
  return category.code || 'Unnamed';
}

export async function loadInvitationTypeFormOptions(apiUrl) {
  const invitationTypes = await fetchInvitationTypeOptions(apiUrl);
  return { invitationTypes };
}

/**
 * Load invitation types and inject into Form.io select fields.
 * No-op for forms that don't have an invitation type select.
 */
export async function prepareFormSchemaWithInvitationSelects(schema, apiUrl, formName = "") {
  if (!schema?.components) {
    return { schema, hasInvitationFormSelects: false };
  }

  const isTarget =
    isInvitationTypesFormName(formName) ||
    formSchemaHasInvitationTypeFormSelects(schema.components);

  if (!isTarget) {
    return { schema, hasInvitationFormSelects: false };
  }

  try {
    const { invitationTypes } = await loadInvitationTypeFormOptions(apiUrl);
    const next = applyInvitationTypeFormSelects(schema, invitationTypes, formName);
    return {
      schema: next,
      hasInvitationFormSelects: true,
      invitationTypes,
    };
  } catch (err) {
    // Options API failure must not block form render.
    console.warn("loadInvitationTypeFormOptions failed:", err);
    return { schema, hasInvitationFormSelects: true };
  }
}
